import React, { useState } from 'react';
import styled, { css } from 'styled-components';
import { animated } from 'react-spring';
import JustificationContainer from '~/components/atom/JustificationContainer';
import Icon from '~/components/atom/Icon';
import useCollapseAnimation from '~/hooks/useCollapseAnimation';
import type { TrackingInfo as TrackingInfoType } from '~/graphql/types';

export type Props = {
  trackingInfo: TrackingInfoType;
};

const text = {
  showMore: 'Meer details',
  showLess: 'Minder details',
};

const TrackingInfoComponent: React.FC<Props> = ({ trackingInfo }) => {
  const { highlights, details } = trackingInfo;
  const [expanded, setExpanded] = useState(false);

  const { innerRef, outer, inner } = useCollapseAnimation({
    isOpen: expanded,
  });

  return (
    <Container>
      <HighlightsRow align="center" wrap="wrap" gap="xs">
        {highlights.map((highlight, index) => (
          <Highlight key={index} highlight={highlight} />
        ))}
      </HighlightsRow>

      {details.length > 0 && (
        <>
          <ToggleButton
            align="center"
            onClick={() => setExpanded(prev => !prev)}
          >
            <Icon
              name={expanded ? 'chevron-up' : 'chevron'}
              margin={[null, 'xxs', null, null]}
              strokeWidth={2}
            />
            {expanded ? text.showLess : text.showMore}
          </ToggleButton>

          <animated.div
            style={{
              opacity: outer.opacity,
              height: outer.height,
              overflow: 'hidden',
              width: '100%',
            }}
          >
            <animated.div ref={innerRef} style={{ y: inner.y, width: '100%' }}>
              <DetailsContainer>
                {details.map((detail, index) => (
                  <DetailRow key={index}>
                    <DetailKey>{detail.key}</DetailKey>
                    <DetailValue>{detail.value}</DetailValue>
                  </DetailRow>
                ))}
              </DetailsContainer>
            </animated.div>
          </animated.div>
        </>
      )}
    </Container>
  );
};

type HighlightProps = {
  highlight: TrackingInfoType['highlights'][number];
};

const Highlight: React.FC<HighlightProps> = ({ highlight }) => {
  const { icon, label, value, link } = highlight;
  const isIconOnly = !label && !value && !!icon;

  const content = (
    <ChipContainer align="center" gap="xxs" $clickable={!!link}>
      {icon && <ChipIcon src={icon} alt={label || ''} />}
      {!isIconOnly && (
        <>
          {label && <ChipLabel>{label}</ChipLabel>}
          {value && <ChipValue>{value}</ChipValue>}
        </>
      )}
    </ChipContainer>
  );

  if (link) {
    return (
      <ChipLink href={link} target="_blank" rel="noopener noreferrer">
        {content}
      </ChipLink>
    );
  }

  return content;
};

const Container = styled.div<{}>(
  ({ theme }) => css`
    border-top: 1px solid ${theme.color('tertiary', 'light')};
    padding: ${theme.space('s')} ${theme.space('base')};
    width: 100%;
  `,
);

const HighlightsRow = styled(JustificationContainer)<{}>(
  () => css`
    width: 100%;
  `,
);

const ChipContainer = styled(JustificationContainer)<{ $clickable: boolean }>(
  ({ theme, $clickable }) => css`
    background-color: ${theme.color('tertiary', 'light')};
    border-radius: ${theme.getTokens().border.radius.base};
    padding: ${theme.space('xxs')} ${theme.space('xs')};
    font-size: ${theme.fontSize('s')};
    max-width: 100%;

    ${$clickable &&
    css`
      &:hover {
        background-color: ${theme.color('tertiary')};
      }
    `}
  `,
);

const ChipIcon = styled.img<{}>(
  () => css`
    width: 16px;
    height: 16px;
    flex-shrink: 0;
  `,
);

const ChipLabel = styled.span<{}>(
  ({ theme }) => css`
    color: ${theme.color('text', 'light')};
    white-space: nowrap;
  `,
);

const ChipValue = styled.span<{}>(
  ({ theme }) => css`
    color: ${theme.color('text')};
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  `,
);

const ChipLink = styled.a<{}>(
  () => css`
    text-decoration: none;
    max-width: 100%;
  `,
);

const ToggleButton = styled(JustificationContainer)<{}>(
  ({ theme }) => css`
    color: ${theme.color('text', 'light')};
    cursor: pointer;
    font-size: ${theme.fontSize('s')};
    padding: ${theme.space('xxs')} 0;
    margin-top: ${theme.space('xxs')};

    &:hover {
      color: ${theme.color('primary', 'light')};
      transition: all 0.2s ease-in;
    }
  `,
);

const DetailsContainer = styled.div<{}>(
  ({ theme }) => css`
    display: flex;
    flex-direction: column;
    gap: ${theme.space('xxs')};
    padding: ${theme.space('xs')} 0 ${theme.space('s')};
    width: 100%;
  `,
);

const DetailRow = styled.div<{}>(
  ({ theme }) => css`
    display: flex;
    gap: ${theme.space('s')};
    font-size: ${theme.fontSize('s')};
    line-height: 1.4;
  `,
);

const DetailKey = styled.span<{}>(
  ({ theme }) => css`
    color: ${theme.color('text', 'light')};
    flex-shrink: 0;
    min-width: 80px;
  `,
);

const DetailValue = styled.span<{}>(
  ({ theme }) => css`
    color: ${theme.color('text')};
    word-break: break-all;
  `,
);

export default TrackingInfoComponent;
